/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import GeneralDialog from "src/components/GeneralDialog";
import { useNavigate } from "react-router-dom";
import { getBundleOptions } from "src/modules/companies/redux";
import { addAlert } from "src/modules/notification/redux";
import resolveBundleId, {
  isMatchedBundleError,
} from "src/modules/bundles/utils/resolve-bundle-id";
import { getErrorMessage } from "../../../../utils/errorMessages";
import {
  getApprovals,
  getAvailablePlans,
  putApproval,
} from "../../redux/action";
import {
  setPage,
  setTypeFilter,
  setStatusFilter,
  clearCompanies,
} from "../../redux/slice";
import ApprovalsPage from "./approvals.page";
import AccessRights from "../../../../configs/constants/accessRights";

// What actually happened, named after the verdict the admin chose. One of these is the only
// message a completed submit produces.
const APPROVAL_DONE_MESSAGE = {
  approve: "Company approved successfully",
  reject: "Company rejected successfully",
  suspend: "Company suspended successfully",
};

export default function ApprovalsContainer() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const industries = useSelector((state) => state.constants.industries);
  const isCompaniesLoading = useSelector(
    (state) =>
      state.loading.inProcess.filter((p) => p.id === getApprovals.typePrefix)
        .length > 0
  );
  const plans = useSelector((state) => state.approvals.plans);
  // Same list the dialog's dropdown is built from (fetched by the dialog container). The
  // submit path re-derives whether the admin's switches still match a bundle rather than
  // trusting a flag the UI passed down.
  const bundleOptions = useSelector((state) => state.companies.bundleOptions);
  // The confirmation dialog stores `() => handleSubmitApproval(...)` when it OPENS, so a
  // closure over `bundleOptions` would resolve every retry against the list as it was at that
  // moment — including after the first attempt created a bundle that is now live. The ref
  // keeps the resolve reading the current list.
  const bundleOptionsRef = useRef(bundleOptions);
  useEffect(() => {
    bundleOptionsRef.current = bundleOptions;
  }, [bundleOptions]);
  // ...and for the same reason, what that dialog submits is read at Proceed time rather than
  // captured at open time. A create that succeeded moves the approval onto the bundle it
  // made (see `adoptCreatedBundle`), and a retry has to submit THAT, not the values the
  // wizard held before the create.
  const submitValuesRef = useRef(null);
  const companies = useSelector((state) => state.approvals.companies);
  const companiesPage = useSelector((state) => state.approvals.companiesPage);
  const totalCompanies = useSelector((state) => state.approvals.totalCompanies);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [itemPerPage, setItemPerPage] = useState(25);
  const filterType = useSelector((state) => state.approvals.filterType);
  const filterStatus = useSelector((state) => state.approvals.filterStatus);
  const [searchValue, setSearchValue] = useState("");
  const [confirmationDialog, setConfirmationDialog] = useState({
    isOpen: false,
    title: "",
    description: "",
    onConfirm: null,
    onCancel: null,
    isLoading: false,
  });
  const canView = useSelector((state) =>
    state.session.accessRights.includes(AccessRights.APPROVAL_VIEW)
  );
  const canEdit = useSelector((state) =>
    state.session.accessRights.includes(AccessRights.APPROVAL_EDIT)
  );

  const typeList = [
    { label: "Company Name", value: "company_name" },
    { label: "Registration Number", value: "reg_number" },
  ];

  const statusList = [
    { label: "Pending", value: "pending" },
    { label: "Rejected", value: "rejected" },
    { label: "Suspended", value: "suspended" },
  ];

  useEffect(() => {
    dispatch(getApprovals({ page: 0, length: itemPerPage }));
    dispatch(getAvailablePlans());
    return () => {
      dispatch(clearCompanies());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(getApprovals({ page: 0, length: itemPerPage }));
  }, [itemPerPage]);

  useEffect(() => {
    if (!canView) {
      navigate("/admin");
    }
  }, [canView]);

  const handleFilter = (values) => {
    const selectedType = typeList.find((type) => type.label === values.type);
    const selectedStatus = statusList.find(
      (status) => status.label === values.status
    );
    setSearchValue(values.searchValue);
    dispatch(setPage(0));
    dispatch(setTypeFilter(selectedType ? selectedType.label : ""));
    dispatch(setStatusFilter(selectedStatus ? selectedStatus.label : ""));
    dispatch(
      getApprovals({
        page: 0,
        length: itemPerPage,
        search: values.searchValue,
        searchColumn: selectedType ? selectedType.value : "",
        statusFilter: selectedStatus ? selectedStatus.value : "",
      })
    );
  };

  const resetFilter = () => {
    setSearchValue("");
    dispatch(setPage(0));
    dispatch(setTypeFilter(""));
    dispatch(setStatusFilter(""));
    dispatch(getApprovals({ page: 0, length: itemPerPage }));
  };

  const onCompanyClick = (company) => {
    setSelectedCompany(company);
  };

  // The bundle the company must end up on — the same three-way resolve every surface uses
  // (unchanged / matches another bundle / has to be created first). An approval is the one
  // caller that also accepts "no bundle": the payload carries null rather than -1.
  const resolveApprovalBundleId = (values, onBundleCreated) => (values.bundle === -1
    ? Promise.resolve(null)
    : resolveBundleId({
      dispatch,
      bundleOptions: bundleOptionsRef.current,
      values,
      onBundleCreated,
    }));

  const handleSubmitApproval = (values, actions) => {
    // Submit the approval, function called after confirmation
    setConfirmationDialog((prev) => ({ ...prev, isLoading: true }));

    const stopSubmitting = () => {
      actions.setSubmitting(false);
      setConfirmationDialog((prev) => ({ ...prev, isLoading: false }));
    };

    const sendApproval = (bundleId) => {
      const approval = {
        id: selectedCompany.company.id,
        status: values.approval,
        isTestingAccount: values.isTestingAccount,
        expireDate: values.expireDate,
        salesEmail: values.salesEmail, // <-- add this
      };

      if (values.approval === "approve") {
        if ([2, 3].includes(selectedCompany.registrationType)) {
          approval.planUuid = values.plan.uuid;
        }
        approval.bundle = bundleId;
      }

      if (values.approval === "reject") {
        approval.remarks = values.remarks;
        approval.rejectModules = values.rejectModules;
      }

      if (values.approval === "suspend") {
        approval.remarks = values.remarks;
      }

      return dispatch(putApproval(approval));
    };

    // The create was accepted, so from here on the company is being approved onto the bundle
    // it just made — not onto the one step 2's dropdown was showing when Approve was pressed.
    // Both copies of the approval move onto it BEFORE putApproval is dispatched:
    //   * the values this confirmation dialog will resubmit, so that a retry after a REFUSED
    //     approve resolves as UNCHANGED and assigns this bundle. Resubmitting the pre-create
    //     values instead makes the switches describe a bundle the dropdown does not name —
    //     the one this save created — which the resolver refuses, for good;
    //   * the wizard behind the dialog, so the Bundle field names the bundle actually being
    //     written rather than the old one for the whole of a create-and-assign.
    // Only the three fields the create changed: `values` is the SUBMIT PAYLOAD, whose
    // expireDate has already been turned from a month count into a date, and writing that
    // back would leave the wizard's own field failing its "number of months" rule.
    // A rejected create never reaches here, so nothing moves and nothing is approved.
    const adoptCreatedBundle = (created) => {
      submitValuesRef.current = {
        ...values,
        bundle: created.id,
        moduleIds: created.moduleIds,
        newBundleName: "",
      };
      if (actions && actions.setValues) {
        actions.setValues((previous) => ({
          ...previous,
          bundle: created.id,
          moduleIds: created.moduleIds,
          newBundleName: "",
        }));
      }
    };

    // Reject/suspend never touch bundles, so they never wait on a create.
    const bundleReady = values.approval === "approve"
      ? resolveApprovalBundleId(values, adoptCreatedBundle)
      : Promise.resolve(null);

    bundleReady
      .then(sendApproval)
      .then((result) => {
        if (putApproval.fulfilled.match(result)) {
          actions.setSubmitting(false);
          setSelectedCompany(null);
          setConfirmationDialog((prev) => ({
            ...prev,
            isLoading: false,
            isOpen: false,
          }));
          // The one outcome message for this submit. An approve whose module set had to be
          // created no longer announces the create separately (resolveBundleId marks it as
          // an interim step), which was both the wrong subject — the admin approved a
          // company, they did not set out to author a bundle — and, on the snackbar's
          // queue, shown for its full 3.5s ahead of any error that followed it.
          dispatch(
            addAlert({
              severity: "success",
              message: APPROVAL_DONE_MESSAGE[values.approval]
                || "Company approval processed successfully",
            })
          );
          // Incidental to the approval, exactly like the companies-list refresh behind the
          // bundle dialog: the wizard has already closed and said what happened. A red
          // "Failed to load approvals list" arriving behind the green one would read as the
          // approval having failed, and it did not — so this refresh keeps quiet. A
          // rejection leaves the list one row out of date, which the next load corrects.
          dispatch(getApprovals({
            page: companiesPage,
            length: itemPerPage,
            suppressErrorAlert: true,
          }));
        } else {
          // Rejected. The approvals middleware has shown the server's reason on the same
          // snackbar, and no green toast has been queued in front of it, so a
          // create-succeeded-approval-refused submit cannot read as an unqualified success.
          // The wizard stays open on the company so the admin can retry — and because a
          // successful create has already put its bundle in the wizard's Bundle field and in
          // `submitValuesRef`, the retry resolves as UNCHANGED and ASSIGNS that bundle
          // instead of trying to create it twice (or, worse, being refused for describing
          // "a different bundle" that this very save authored).
          stopSubmitting();
        }
      })
      .catch((error) => {
        // The bundle create failed (duplicate module set, name taken, ...) and the bundles
        // middleware has already shown the server's message. putApproval was never
        // dispatched: the approval must not half-happen.
        //
        // Except for one rejection nobody else speaks for: the resolver refusing to assign a
        // bundle the dropdown is not showing. The wizard's schema blocks that before this
        // runs, so reaching here means the guard was bypassed — which is exactly when a
        // swallowed rejection would read as "the button does nothing". Say it out loud.
        if (isMatchedBundleError(error)) {
          dispatch(addAlert({ severity: "error", message: error.message }));
        }
        stopSubmitting();
      });
  };

  const onCompanyApprovalDialogSubmitClick = (values, actions) => {
    submitValuesRef.current = values;
    setConfirmationDialog({
      isOpen: true,
      description: `Are you sure you want to ${values.approval} this company?`,
      title:
        values.approval === "approve"
          ? "Approve Company"
          : values.approval === "reject"
          ? "Reject Company"
          : "Suspend Company",
      // Read at Proceed time, not captured here: a first attempt whose create succeeded and
      // whose approve was refused has already moved this ref onto the created bundle.
      onConfirm: () => handleSubmitApproval(submitValuesRef.current, actions),
      onCancel: () => actions.setSubmitting(false), // reset the form submitting on closing confirmation dialog
      isLoading: false,
    });
  };

  const onCompanyApprovalDialogCloseClick = () => {
    setSelectedCompany(null);
  };

  const onCompaniesPageClick = (page) => {
    if (companiesPage !== page) {
      dispatch(getApprovals({ page, length: itemPerPage }));
    }
  };

  const handleItemPerPageChange = (value) => {
    setItemPerPage(value);
  };

  const handleConfirmationDialogClose = () => {
    if (!confirmationDialog.isLoading) {
      confirmationDialog.onCancel();
      setConfirmationDialog({ ...confirmationDialog, isOpen: false }); // Close the confirmation dialog
    }
  };

  const handleConfirmationDialogProceed = () => {
    confirmationDialog.onConfirm(); // Call the confirmed function handleSubmitApproval
    // Don't close the dialog here - let handleSubmitApproval handle closing after completion
  };

  const isFetching = false;

  return (
    <>
      <ApprovalsPage
        industries={industries}
        isCompaniesLoading={isCompaniesLoading}
        companies={companies}
        totalCompanies={totalCompanies}
        companiesPage={companiesPage}
        selectedCompany={selectedCompany}
        onCompanyClick={onCompanyClick}
        onCompanyApprovalDialogSubmitClick={onCompanyApprovalDialogSubmitClick}
        onCompanyApprovalDialogCloseClick={onCompanyApprovalDialogCloseClick}
        onCompaniesPageClick={onCompaniesPageClick}
        handleItemPerPageChange={handleItemPerPageChange}
        itemPerPage={itemPerPage}
        plans={plans}
        isFetching={isFetching}
        typeList={typeList}
        statusList={statusList}
        filterState={{
          type: filterType || "Company Name",
          status: filterStatus,
          searchValue,
        }}
        handleFilter={handleFilter}
        resetFilter={resetFilter}
        canEdit={canEdit}
      />
      <GeneralDialog
        isLoading={confirmationDialog.isLoading}
        isOpen={confirmationDialog.isOpen}
        handleClose={handleConfirmationDialogClose}
        handleProceed={handleConfirmationDialogProceed}
        title={confirmationDialog.title}
        description={confirmationDialog.description}
        type="danger"
      />
    </>
  );
}
